import {
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal,
} from "@internationalized/date"
import { redirect } from "@tanstack/react-router"
import { CheckoutFailReason } from "ipctg-inflow-types/InFlowEnums"

import { ExpectedError } from "./expectedErrors"

/**
 * Seconds until an absolute timestamp, negative once it has passed. Null for a
 * missing timestamp, so callers can tell "no hold" apart from "hold gone".
 */
export function secondsUntil(
  timestamp: string | null | undefined
): number | null {
  if (!timestamp) return null
  return Math.floor(
    parseAbsoluteToLocal(timestamp).compare(now(getLocalTimeZone())) / 1000
  )
}

export function isConcurrentSessionSentinel(
  holdReleaseTimestamp: string | null | undefined
): boolean {
  if (!holdReleaseTimestamp) return false
  return (
    Math.abs(
      parseAbsoluteToLocal(holdReleaseTimestamp).compare(
        parseAbsoluteToLocal(CONCURRENT_SESSION_SENTINEL)
      )
    ) <= CONCURRENT_SESSION_SENTINEL_WINDOW_MS
  )
}

/**
 * What the server sends for holdReleaseTimestamp when another purchase session
 * is already open.
 */
const CONCURRENT_SESSION_SENTINEL = "2000-01-01T00:00:00.000Z"

const CONCURRENT_SESSION_SENTINEL_WINDOW_MS = 24 * 60 * 60 * 1000

export function isHoldExpired(
  holdReleaseTimestamp: string | null | undefined
): boolean {
  const remaining = secondsUntil(holdReleaseTimestamp)
  return remaining !== null && remaining <= 0
}

export function isReserveTimeoutError(error: unknown): error is ExpectedError {
  return (
    error instanceof ExpectedError &&
    error.failReason === CheckoutFailReason.ReservationHoldExpired
  )
}

export interface ReserveDateParams {
  qrLookupID: string
  epoch: string
  calendarID: string
  year: string
  month: string
  day: string
}

export const RESERVE_CONCURRENT_SESSION_ROUTE =
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/in-progress"

/** The guard page a timed-out reservation lands on. */
export const RESERVE_TIMEOUT_ROUTE =
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/expired"

export function redirectToReserveTimeout(params: ReserveDateParams): never {
  throw redirect({ to: RESERVE_TIMEOUT_ROUTE, params })
}

export function redirectToConcurrentSession(params: ReserveDateParams): never {
  throw redirect({ to: RESERVE_CONCURRENT_SESSION_ROUTE, params })
}
